import { Bell, Check } from "lucide-react";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { resolveNotificationUrl } from "@/lib/notificationUrl";

const priorityIcon: Record<string, string> = {
  urgent: "🔴",
  high: "🟡",
  normal: "🔵",
  low: "⚪",
};

const NotifItem = ({
  n,
  onClick,
  onMarkRead,
}: {
  n: Notification;
  onClick: (n: Notification) => void;
  onMarkRead: (id: string) => void;
}) => {
  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-accent/50",
        n.status === "unread" && "bg-primary/5"
      )}
      onClick={() => onClick(n)}
    >
      <span className="text-sm mt-0.5">{priorityIcon[n.priority] ?? "🔵"}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{n.title}</p>
        {n.message && (
          <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
        )}
        {!n.message && n.entity_label && (
          <p className="text-xs text-muted-foreground truncate">{n.entity_label}</p>
        )}
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {n.action_label && (
          <Badge variant="outline" className="text-[10px]">
            {n.action_label}
          </Badge>
        )}
        {n.status === "unread" && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            title="Mark as read"
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead(n.id);
            }}
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const NotificationBell = () => {
  const { data: notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const navigate = useTabNavigate();

  const handleClick = async (n: Notification) => {
    if (n.status === "unread") {
      await markRead.mutateAsync(n.id);
    }
    const url = resolveNotificationUrl(n);
    if (url) navigate(url);
  };

  const recent = notifications?.filter((n) => n.status === "unread").slice(0, 8) ?? [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => markAllRead.mutate()}>
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {recent.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              ✅ All caught up
            </div>
          ) : (
            recent.map((n) => (
              <NotifItem
                key={n.id}
                n={n}
                onClick={handleClick}
                onMarkRead={(id) => markRead.mutate(id)}
              />
            ))
          )}
        </div>
        <div className="border-t px-3 py-2">
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => navigate("/controller/notifications")}>
            View All Notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
